import type { Coordinate, RenderPointType, RouteTransportTypes } from "@qubictron/maplib";
import { isNullable, Nullable, ValueOf } from "shared/lib/typeCheck.lib";

type Card = {
  currentPin: Nullable<Coordinate>;
  fromDirection: Nullable<string>;
  toDirection: Nullable<string>;
  reference: Nullable<string>;
};

type MapPointsProps = {
  searchCards: Card[];
  savedCards: Card[];
  type: ValueOf<typeof RouteTransportTypes>;
};

type PointType = {
  reference: Nullable<string>;
  fromDirection: Nullable<string>;
  toDirection: Nullable<string>;
} & RenderPointType;

export class MapPoints {
  private searchCards: Card[];
  private savedCards: Card[];
  private type: ValueOf<typeof RouteTransportTypes>;
  points: PointType[] = [];

  constructor({ searchCards, savedCards, type }: MapPointsProps) {
    this.searchCards = searchCards;
    this.savedCards = savedCards;
    this.type = type;
    this.setPoints();
  }

  private toPoint(card: Card): PointType | null {
    if (!card.currentPin?.lat || !card.currentPin?.lng) return null;
    const type = this.type;
    const coordinate = { lng: card.currentPin.lng, lat: card.currentPin.lat };
    const fromDirection = card.fromDirection;
    const toDirection = card.toDirection;

    return {
      reference: card.reference,
      transportType: type,
      coordinate,
      fromDirection,
      toDirection,
    };
  }

  setPoints() {
    if (!this.searchCards.length && !this.savedCards.length) return;

    const points = [...this.searchCards, ...this.savedCards]
      .filter(this.normalizePoint)
      .reduce<PointType[]>((acc, card) => {
        const point = this.toPoint(card);
        return point ? [...acc, point] : acc;
      }, []);

    const pointMap = new Map<string, PointType>();

    points.forEach((point) => {
      const key = `${point.coordinate.lng},${point.coordinate.lat}`;
      const existing = pointMap.get(key);

      if (existing) {
        pointMap.set(key, { ...existing, reference: point.reference });
      } else {
        pointMap.set(key, point);
      }
    });

    this.points = Array.from(pointMap.values());
  }

  private normalizePoint = (point: Card): Card | null => {
    if (isNullable(point.currentPin?.lat) || isNullable(point.currentPin?.lng)) {
      if (__DEV__) {
        console.error("Invalid point data", point);
      }
      return null;
    }

    return {
      ...point,
      currentPin: {
        lat: point.currentPin.lat,
        lng: point.currentPin.lng,
      },
    };
  };
}
